import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, map, tap, catchError, of } from 'rxjs';
import {
  IAuthService,
  AuthState,
  AuthResult,
} from '@infinitform/platform-core';
import { CLOUD_PLATFORM_CONFIG } from '../config';

interface LoginResponse {
  access_token: string;
  email: string;
  role: string;
  permissions: string[];
  subscription_status: string;
  firstname?: string;
  lastname?: string;
  user_id?: string;
}

@Injectable()
export class AuthCloudService implements IAuthService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(CLOUD_PLATFORM_CONFIG);
  private readonly authState$ = new BehaviorSubject<AuthState>({
    authenticated: false,
  });

  isAuthenticated(): Observable<boolean> {
    return this.authState$.pipe(map((state) => state.authenticated));
  }

  getAuthState(): Observable<AuthState> {
    return this.authState$.asObservable();
  }

  login(credentials?: unknown): Observable<AuthResult> {
    return this.http
      .post<LoginResponse>(
        `${this.config.apiBaseUrl}/auth/login`,
        credentials,
        { withCredentials: true }
      )
      .pipe(
        tap((response) => {
          this.authState$.next({
            authenticated: true,
            user: {
              id: response.user_id ?? response.email,
              name:
                [response.firstname, response.lastname]
                  .filter(Boolean)
                  .join(' ') || response.email,
              email: response.email,
            },
            licenseStatus: 'active',
          });
        }),
        map(() => ({ success: true })),
        catchError((error) => of({ success: false, error: error.message }))
      );
  }

  logout(): Observable<void> {
    return this.http
      .post<void>(`${this.config.apiBaseUrl}/auth/logout`, {}, { withCredentials: true })
      .pipe(
        tap(() => {
          this.authState$.next({ authenticated: false });
        })
      );
  }
}
